import { isInsideFeedArea } from "../feedAreas";
import type { AreaPosition } from "../areaAssign";
import type { FeedArea } from "../feedAreas";
import type { BoardDevice, BoardSuspect, FindBoardSuspects } from "./types";

// A device this many times further from its circuit than the circuit's devices usually sit from each other.
const FAR_FACTOR = 4;
const FAR_MIN_DEVICES = 3;

const distance = (a: AreaPosition, b: AreaPosition): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

const nearestNeighbour = (
  device: BoardDevice,
  devices: BoardDevice[],
): number =>
  devices.reduce(
    (best, other) =>
      other.destinationId === device.destinationId
        ? best
        : Math.min(best, distance(device.position, other.position)),
    Number.POSITIVE_INFINITY,
  );

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

const zoneAt = (point: AreaPosition, zones: FeedArea[]): FeedArea | undefined =>
  [...zones].reverse().find((area) => isInsideFeedArea({ point, area }));

export const findBoardSuspects: FindBoardSuspects = ({
  board,
  isMain,
  circuitIds,
  devicesByCircuit,
  feedingBoardByCircuit,
  systemByCircuit = {},
  zones,
  isSourceLive,
  zoneTakes,
}) => {
  const takes = (zoneBoard: string, circuitId: string): boolean =>
    zoneTakes
      ? zoneTakes(zoneBoard, circuitId)
      : (systemByCircuit[circuitId] ?? "power") === "power";
  const liveZones = zones.filter((area) => isSourceLive(area.sourceLabel));
  const suspects: BoardSuspect[] = [];
  const seen = new Set<string>();
  const add = (suspect: BoardSuspect) => {
    if (seen.has(suspect.destinationId)) return;
    seen.add(suspect.destinationId);
    suspects.push(suspect);
  };

  circuitIds.forEach((circuitId) => {
    const devices = devicesByCircuit[circuitId] ?? [];
    devices.forEach((device) => {
      const zone = zoneAt(
        device.position,
        liveZones.filter((area) => takes(area.sourceLabel, circuitId)),
      );
      if (zone && zone.sourceLabel !== board) {
        add({
          ...device,
          circuitId,
          reason: "otherZone",
          otherBoard: zone.sourceLabel,
        });
      }
    });

    if (devices.length < FAR_MIN_DEVICES) return;
    const gaps = devices.map((device) => nearestNeighbour(device, devices));
    const usual = median(gaps);
    if (usual <= 0) return;
    devices.forEach((device, index) => {
      if (gaps[index] > usual * FAR_FACTOR) {
        add({ ...device, circuitId, reason: "farFromCircuit" });
      }
    });
  });

  // The main board's zone is everything no other zone claims, so only a drawn zone can hold a stranger.
  if (!isMain) {
    const ownZones = liveZones.filter((area) => area.sourceLabel === board);
    Object.entries(devicesByCircuit).forEach(([circuitId, devices]) => {
      const feeder = feedingBoardByCircuit[circuitId];
      if (!feeder || feeder === board || !takes(board, circuitId)) return;
      devices.forEach((device) => {
        if (
          ownZones.some((area) =>
            isInsideFeedArea({ point: device.position, area }),
          )
        ) {
          add({
            ...device,
            circuitId,
            reason: "foreignInZone",
            otherBoard: feeder,
          });
        }
      });
    });
  }

  return suspects;
};
